import { create } from 'zustand';
import { chatsAPI, queryTypesAPI } from '../services/api';

interface TurnAttachment {
  id: number;
  original_filename: string;
  mime_type: string;
  file_size: number;
}

interface Turn {
  id: number;
  user_query: string;
  mode: string;
  judge_response: string | null;
  total_cost: number;
  total_time_ms: number;
  created_at: string;
  attachments?: TurnAttachment[];
}

interface Chat {
  id: number;
  title: string;
  query_type_id: number;
  project_id: number | null;
  turns: Turn[];
  created_at: string;
}

interface QueryType {
  id: number;
  name: string;
  description: string;
  engines: any[];
  is_custom: boolean;
}

interface EngineStatus {
  engine_name: string;
  model_name: string;
  status: 'waiting' | 'sent' | 'received' | 'error' | 'skipped';
  round: number;
}

interface ChatState {
  chats: Chat[];
  currentChat: Chat | null;
  queryTypes: QueryType[];
  isLoading: boolean;
  isQuerying: boolean;
  engineStatuses: EngineStatus[];
  currentTaskId: string | null;
  queryMode: 'quick' | 'deliberation';
  currentRound: number;
  totalRounds: number;
  elapsedTime: number;
  runningCost: number;
  isJudgePhase: boolean;
  roundStartTime: number | null;

  fetchChats: () => Promise<void>;
  fetchChat: (chatId: number) => Promise<void>;
  fetchQueryTypes: () => Promise<void>;
  createChat: (queryTypeId: number, projectId?: number) => Promise<Chat>;
  createChatWithQuery: (queryTypeId: number, query: string, mode: 'quick' | 'deliberation', projectId?: number, attachmentIds?: number[]) => Promise<Chat>;
  deleteChat: (chatId: number) => Promise<void>;
  renameChat: (chatId: number, title: string) => Promise<void>;
  submitQuery: (chatId: number, query: string, attachmentIds?: number[]) => Promise<void>;
  setQueryMode: (mode: 'quick' | 'deliberation') => void;
  clearCurrentChat: () => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  chats: [],
  currentChat: null,
  queryTypes: [],
  isLoading: false,
  isQuerying: false,
  engineStatuses: [],
  currentTaskId: null,
  queryMode: 'quick',
  currentRound: 1,
  totalRounds: 1,
  elapsedTime: 0,
  runningCost: 0,
  isJudgePhase: false,
  roundStartTime: null,

  fetchChats: async () => {
    set({ isLoading: true });
    try {
      const response = await chatsAPI.list();
      set({ chats: response.data, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  fetchChat: async (chatId: number) => {
    set({ isLoading: true });
    try {
      const response = await chatsAPI.get(chatId);
      const chat = response.data;

      // Set queryMode from last turn's mode (if chat has turns)
      if (chat.turns && chat.turns.length > 0) {
        const lastTurn = chat.turns[chat.turns.length - 1];
        set({ queryMode: lastTurn.mode as 'quick' | 'deliberation' });
      }

      set({ currentChat: chat, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  fetchQueryTypes: async () => {
    try {
      const response = await queryTypesAPI.list();
      const queryTypes = response.data as QueryType[];

      // Sort query types:
      // 1. System defaults (is_custom=false, name !== "Custom") alphabetically
      // 2. User-made custom types (is_custom=true) alphabetically
      // 3. The "Custom" placeholder absolutely last
      const sorted = queryTypes
        .sort((a, b) => {
          // "Custom" placeholder always goes absolutely last
          const aIsCustomPlaceholder = a.name === 'Custom';
          const bIsCustomPlaceholder = b.name === 'Custom';

          if (aIsCustomPlaceholder && !bIsCustomPlaceholder) return 1;
          if (!aIsCustomPlaceholder && bIsCustomPlaceholder) return -1;

          // System defaults before user-made
          if (!a.is_custom && b.is_custom) return -1;
          if (a.is_custom && !b.is_custom) return 1;

          // Within same category, sort alphabetically
          return a.name.localeCompare(b.name);
        })
        .map((qt) => ({
          ...qt,
          // Prefix user-made custom types with asterisk for visual distinction
          name: qt.is_custom ? `* ${qt.name}` : qt.name,
        }));

      set({ queryTypes: sorted });
    } catch (error) {
      console.error('Failed to fetch query types:', error);
    }
  },

  createChat: async (queryTypeId: number, projectId?: number) => {
    const response = await chatsAPI.create({
      query_type_id: queryTypeId,
      project_id: projectId,
    });
    const newChat = response.data;
    set((state) => ({ chats: [newChat, ...state.chats] }));
    return newChat;
  },

  createChatWithQuery: async (queryTypeId: number, query: string, mode: 'quick' | 'deliberation', projectId?: number, attachmentIds?: number[]) => {
    const { queryTypes } = get();

    // Initialize engine statuses for UI feedback
    const queryType = queryTypes.find((qt) => qt.id === queryTypeId);
    const initialStatuses: EngineStatus[] = queryType?.engines.map((eng: any) => ({
      engine_name: eng.engine_name,
      model_name: eng.model_name,
      status: 'sent' as const,
      round: 1,
    })) || [];

    const totalRounds = mode === 'deliberation' ? 2 : 1;
    set({
      isQuerying: true,
      engineStatuses: initialStatuses,
      currentRound: 1,
      totalRounds,
      elapsedTime: 0,
      runningCost: 0,
      isJudgePhase: false,
      roundStartTime: Date.now(),
    });

    try {
      // Create chat and start query execution immediately
      const response = await chatsAPI.createWithQuery({
        query_type_id: queryTypeId,
        query,
        mode,
        project_id: projectId,
        attachment_ids: attachmentIds,
      });

      const { chat: newChat, task_id: taskId } = response.data;

      // Add chat to list and set as current
      set((state) => ({
        chats: [{ ...newChat, turns: [] }, ...state.chats],
        currentChat: { ...newChat, turns: [] },
        currentTaskId: taskId,
      }));

      // Poll for real-time status updates
      let completed = false;
      let hasAnyResponse = false;

      while (!completed) {
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const statusResponse = await chatsAPI.getQueryStatus(newChat.id, taskId);
        const { status, result } = statusResponse.data;

        if (status === 'PROCESSING' && result) {
          // Update progress from backend
          if (result.engine_statuses) {
            const updatedStatuses: EngineStatus[] = result.engine_statuses.map((e: any) => ({
              engine_name: e.engine_name,
              model_name: e.model_name || '',
              status: e.status as EngineStatus['status'],
              round: e.round || 1,
            }));

            const { currentRound: prevRound, isJudgePhase: prevJudge } = get();
            const newRound = result.current_round || 1;
            const newJudge = result.is_judge_phase || false;

            // Reset roundStartTime on phase change
            const roundStartTime = (newRound !== prevRound || newJudge !== prevJudge)
              ? Date.now()
              : get().roundStartTime;

            set({
              engineStatuses: updatedStatuses,
              currentRound: newRound,
              totalRounds: result.total_rounds || totalRounds,
              elapsedTime: result.elapsed_ms || 0,
              runningCost: result.running_cost || 0,
              isJudgePhase: newJudge,
              roundStartTime,
            });
          }
        } else if (status === 'SUCCESS') {
          completed = true;

          // Update engine statuses from result
          if (result?.engine_responses) {
            const updatedStatuses = result.engine_responses.map((r: any) => ({
              engine_name: r.engine_name,
              model_name: r.model_name,
              status: r.status === 'success' ? 'received' : 'error',
              round: r.round,
            }));
            set({ engineStatuses: updatedStatuses });

            // Check if any engine completed successfully
            hasAnyResponse = updatedStatuses.some((s: EngineStatus) => s.status === 'received');
          }

          // Save result and refresh chat
          await chatsAPI.saveQueryResult(newChat.id, taskId);
          await get().fetchChat(newChat.id);
        } else if (status === 'FAILURE') {
          completed = true;
          throw new Error(result?.error_message || 'Query execution failed');
        }
      }

      // If no engines completed successfully, delete the empty chat
      if (!hasAnyResponse) {
        await chatsAPI.delete(newChat.id);
        set((state) => ({
          chats: state.chats.filter((c) => c.id !== newChat.id),
          currentChat: null,
        }));
        throw new Error('All AI engines failed to respond. Please try again with a different query or check your connection.');
      }

      return get().currentChat!;
    } catch (error: any) {
      const errorMessage = error?.message || 'Query failed';

      // Update engine statuses to show failure
      const failedStatuses: EngineStatus[] = initialStatuses.map((status) => ({
        ...status,
        status: 'error' as const,
      }));
      set({ engineStatuses: failedStatuses });

      // Don't need to clean up chat here - either it was never created or already cleaned up above
      throw error;
    } finally {
      set({
        isQuerying: false,
        currentTaskId: null,
        elapsedTime: 0,
        runningCost: 0,
        isJudgePhase: false,
        roundStartTime: null,
      });
    }
  },

  deleteChat: async (chatId: number) => {
    await chatsAPI.delete(chatId);
    set((state) => ({
      chats: state.chats.filter((c) => c.id !== chatId),
      currentChat: state.currentChat?.id === chatId ? null : state.currentChat,
    }));
  },

  renameChat: async (chatId: number, title: string) => {
    await chatsAPI.update(chatId, { title });
    set((state) => ({
      chats: state.chats.map((c) =>
        c.id === chatId ? { ...c, title } : c
      ),
      currentChat:
        state.currentChat?.id === chatId
          ? { ...state.currentChat, title }
          : state.currentChat,
    }));
  },

  submitQuery: async (chatId: number, query: string, attachmentIds?: number[]) => {
    const { queryMode, currentChat, queryTypes } = get();

    // Initialize engine statuses
    const queryType = queryTypes.find((qt) => qt.id === currentChat?.query_type_id);
    const initialStatuses: EngineStatus[] = queryType?.engines.map((eng: any) => ({
      engine_name: eng.engine_name,
      model_name: eng.model_name,
      status: 'waiting' as const,
      round: 1,
    })) || [];

    const totalRounds = queryMode === 'deliberation' ? 2 : 1;
    set({
      isQuerying: true,
      engineStatuses: initialStatuses,
      currentRound: 1,
      totalRounds,
      elapsedTime: 0,
      runningCost: 0,
      isJudgePhase: false,
      roundStartTime: Date.now(),
    });

    try {
      // Submit query
      const submitResponse = await chatsAPI.submitQuery(chatId, query, queryMode, attachmentIds);
      const taskId = submitResponse.data.task_id;
      set({ currentTaskId: taskId });

      // Poll for completion
      let completed = false;
      while (!completed) {
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const statusResponse = await chatsAPI.getQueryStatus(chatId, taskId);
        const { status, result } = statusResponse.data;

        if (status === 'PROCESSING' && result) {
          // Update progress from backend
          if (result.engine_statuses) {
            const updatedStatuses: EngineStatus[] = result.engine_statuses.map((e: any) => ({
              engine_name: e.engine_name,
              model_name: e.model_name || '',
              status: e.status as EngineStatus['status'],
              round: e.round,
            }));

            // Detect phase transitions for progress bar reset
            const prevRound = get().currentRound;
            const prevJudge = get().isJudgePhase;
            const newRound = result.round === 'judge' ? prevRound : (result.round || 1);
            const newJudge = result.is_judge_phase || false;

            // Reset roundStartTime on phase change
            const roundStartTime = (newRound !== prevRound || newJudge !== prevJudge)
              ? Date.now()
              : get().roundStartTime;

            set({
              engineStatuses: updatedStatuses,
              currentRound: newRound,
              totalRounds: result.total_rounds || totalRounds,
              elapsedTime: result.elapsed_ms || 0,
              runningCost: result.running_cost || 0,
              isJudgePhase: newJudge,
              roundStartTime,
            });
          }
        } else if (status === 'SUCCESS') {
          completed = true;

          // Update engine statuses from result
          if (result?.engine_responses) {
            const updatedStatuses = result.engine_responses.map((r: any) => ({
              engine_name: r.engine_name,
              model_name: r.model_name,
              status: r.status === 'success' ? 'received' : 'error',
              round: r.round,
            }));
            set({ engineStatuses: updatedStatuses });
          }

          // Save result and refresh chat
          await chatsAPI.saveQueryResult(chatId, taskId);
          await get().fetchChat(chatId);
        } else if (status === 'FAILURE') {
          completed = true;
          throw new Error(result?.error || 'Query failed');
        }
      }
    } finally {
      set({
        isQuerying: false,
        currentTaskId: null,
        elapsedTime: 0,
        runningCost: 0,
        isJudgePhase: false,
        roundStartTime: null,
      });
    }
  },

  setQueryMode: (mode) => set({ queryMode: mode }),

  clearCurrentChat: () => set({ currentChat: null, engineStatuses: [] }),
}));
